import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardActionArea,
  Chip,
  LinearProgress,
  Stack,
  Typography,
  Skeleton,
  Divider,
} from '@mui/material';
import {
  Inbox as InboxIcon,
  Workspaces as WorkspacesIcon,
  ViewKanban as KanbanIcon,
  GridView as GridIcon,
  Schedule as ScheduleIcon,
  Loop as LoopIcon,
  Warning as WarningIcon,
  AccountBalance as BillingIcon,
  PersonSearch as PersonIcon,
  Gavel as GavelIcon,
  ArrowForward as ArrowIcon,
  Description as DescriptionIcon,
  FolderOpen as FolderIcon,
  EventBusy as EventBusyIcon,
  AttachFile as AttachIcon,
  Article as ArticleIcon,
  AccessTime as AccessTimeIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Timeline as TimelineIcon,
  AddCircleOutline as AddCircleOutlineIcon,
} from '@mui/icons-material';
import { MODULE_CONFIGS, type ModuleId } from '../theme/moduleColors';
import { useUiLanguageStore } from '../store/uiLanguageStore';
import {
  useSetupKanbanPreview,
  useEmailInboxSummary,
  usePortfolioBurnDown,
  useUpcomingRenewals,
  useSlaBreaches,
  useConflictReviewSummary,
  useMyDocsExpiring,
  isUnavailable,
} from '../api/dashboard';
import { useDocuments } from '../api/documents';
import { useSetupMatters } from '@setup/hooks/useSetupMatters';
import { useTransactionTypes } from '@setup/hooks/useTransactionTypes';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ModuleHomeProps { moduleId: string; }

interface QuickLink {
  path: string;
  label: string;
  hint?: string;
  icon: React.ReactElement;
}

// ---------------------------------------------------------------------------
// Shared primitives
// ---------------------------------------------------------------------------

const CARD_BORDER = '1px solid #E3E7EC';
const SUBTLE_BG = '#FAFAF7';

interface KpiTileProps {
  label: string;
  value: React.ReactNode;
  hint?: string;
  loading?: boolean;
  intent?: 'default' | 'warn' | 'danger';
  accent: string;
}

const KpiTile: React.FC<KpiTileProps> = ({ label, value, hint, loading, intent = 'default', accent }) => {
  const intentColor =
    intent === 'danger' ? '#991B1B' : intent === 'warn' ? '#92400E' : '#0E1116';
  return (
    <Box
      sx={{
        bgcolor: '#FFFFFF',
        border: CARD_BORDER,
        borderTop: `2px solid ${accent}`,
        px: 2,
        py: 1.25,
        minHeight: 78,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <Typography variant="caption" sx={{ color: '#4A5568', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.68rem' }}>
        {label}
      </Typography>
      {loading ? (
        <Skeleton variant="text" width={60} height={28} />
      ) : (
        <Typography variant="h6" fontWeight={700} sx={{ color: intentColor, lineHeight: 1.1, fontSize: '1.25rem' }}>
          {value}
        </Typography>
      )}
      {hint ? (
        <Typography variant="caption" sx={{ color: '#506070', fontSize: '0.7rem' }}>{hint}</Typography>
      ) : null}
    </Box>
  );
};

interface QuickTileProps {
  link: QuickLink;
  accent: string;
  count?: React.ReactNode;
  onClick: (path: string) => void;
}

const QuickTile: React.FC<QuickTileProps> = ({ link, accent, count, onClick }) => (
  <Card
    sx={{
      bgcolor: '#FFFFFF',
      border: CARD_BORDER,
      transition: 'border-color 120ms ease, box-shadow 120ms ease',
      '&:hover': { borderColor: accent, boxShadow: `0 1px 3px ${accent}1F` },
    }}
  >
    <CardActionArea onClick={() => onClick(link.path)} sx={{ px: 2, py: 1.5 }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box sx={{ color: accent, display: 'flex' }}>{link.icon}</Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#0E1116', fontSize: '0.85rem' }}>
              {link.label}
            </Typography>
            {count != null ? (
              <Chip
                size="small"
                label={count}
                sx={{ height: 18, fontSize: '0.68rem', bgcolor: `${accent}14`, color: accent, fontWeight: 600 }}
              />
            ) : null}
          </Stack>
          {link.hint ? (
            <Typography variant="caption" sx={{ color: '#4A5568', fontSize: '0.72rem' }}>{link.hint}</Typography>
          ) : null}
        </Box>
        <ArrowIcon sx={{ color: '#A0AEC0', fontSize: 16 }} />
      </Stack>
    </CardActionArea>
  </Card>
);

// ---------------------------------------------------------------------------
// Module-specific bodies
// ---------------------------------------------------------------------------

function safeArray<T>(data: T[] | { __unavailable: true } | undefined): T[] {
  if (!data) return [];
  if (isUnavailable(data)) return [];
  return data as T[];
}

// --- Jylaw -----------------------------------------------------------------

const JylawBody: React.FC<{ accent: string; onNav: (p: string) => void; rtl: boolean }> = ({ accent, onNav, rtl }) => {
  const emails = useEmailInboxSummary();
  const burn = usePortfolioBurnDown();
  const setup = useSetupKanbanPreview();
  const sla = useSlaBreaches();
  const conflict = useConflictReviewSummary();

  const emailsList = safeArray(emails.data);
  const burnList = safeArray(burn.data);
  const setupList = safeArray(setup.data);
  const slaList = safeArray(sla.data);
  const conflictData = (conflict.data && !isUnavailable(conflict.data)) ? conflict.data : null;

  const atRisk = burnList.filter((r) => r.pct_consumed >= 90).length;
  const proposed = emailsList.filter((e) => e.status === 'proposed' || e.status === 'classified').length;

  const links: QuickLink[] = [
    { path: '/dashboard', label: rtl ? 'لوحة المدير' : 'Manager Dashboard', hint: rtl ? 'كل الويدجتس في صفحة واحدة' : 'All widgets in one place', icon: <WorkspacesIcon fontSize="small" /> },
    { path: '/email-inbox', label: rtl ? 'صندوق البريد' : 'Email Triage', hint: rtl ? 'تصنيف ومراجعة بشرية' : 'Classification + HITL review', icon: <InboxIcon fontSize="small" /> },
    { path: '/admin/billing-entities', label: rtl ? 'كيانات الفوترة' : 'Billing Entities', hint: rtl ? '٣ كيانات تحت المجموعة' : '3 entities under the group', icon: <BillingIcon fontSize="small" /> },
    { path: '/ocr-review', label: rtl ? 'مراجعة OCR' : 'OCR Review', hint: rtl ? 'استخراج تواريخ بأقل من ٠٫٩٥' : 'Extractions below 0.95 confidence', icon: <DescriptionIcon fontSize="small" /> },
  ];

  return (
    <>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2 }}>
        <KpiTile
          accent={accent}
          label={rtl ? 'بريد بانتظار المراجعة' : 'Awaiting triage'}
          value={emails.isLoading ? '—' : proposed}
          hint={rtl ? 'بحاجة لقرار' : 'needs decision'}
          loading={emails.isLoading}
        />
        <KpiTile
          accent={accent}
          label={rtl ? 'تجاوز SLA' : 'SLA breaches'}
          value={sla.isLoading ? '—' : slaList.length}
          hint={rtl ? 'بريد لم يُعالج خلال ٢٤ ساعة' : 'emails > 24h unactioned'}
          loading={sla.isLoading}
          intent={slaList.length > 0 ? 'danger' : 'default'}
        />
        <KpiTile
          accent={accent}
          label={rtl ? 'عقود قاربت السقف' : 'Retainers at risk'}
          value={burn.isLoading ? '—' : atRisk}
          hint={rtl ? '٩٠٪ أو أكثر من الساعات' : '≥90% hours consumed'}
          loading={burn.isLoading}
          intent={atRisk > 0 ? 'warn' : 'default'}
        />
        <KpiTile
          accent={accent}
          label={rtl ? 'معاملات قيد التنفيذ' : 'Setup in-flight'}
          value={setup.isLoading ? '—' : setupList.length}
          hint={rtl ? 'مفتوحة الآن' : 'currently open'}
          loading={setup.isLoading}
        />
      </Box>

      <Typography variant="overline" sx={{ color: '#4A5568', letterSpacing: '0.06em', display: 'block', mb: 0.75 }}>
        {rtl ? 'إجراءات سريعة' : 'Quick actions'}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.25, mb: 2 }}>
        {links.map((l) => (
          <QuickTile
            key={l.path}
            link={l}
            accent={accent}
            count={
              l.path === '/email-inbox' ? proposed :
              l.path === '/ocr-review' && conflictData ? undefined :
              undefined
            }
            onClick={onNav}
          />
        ))}
      </Box>

      <RecentEmailsPanel rtl={rtl} accent={accent} emails={emailsList.slice(0, 5)} loading={emails.isLoading} onNav={onNav} />
    </>
  );
};

// --- Setup -----------------------------------------------------------------

const SetupBody: React.FC<{ accent: string; onNav: (p: string) => void; rtl: boolean }> = ({ accent, onNav, rtl }) => {
  const setup = useSetupKanbanPreview();
  const sla = useSlaBreaches();
  const setupList = safeArray(setup.data);
  const slaList = safeArray(sla.data);

  const byPhase = setupList.reduce<Record<string, number>>((acc, m) => {
    const k = m.current_phase || '—';
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});
  const topPhases = Object.entries(byPhase).sort((a, b) => b[1] - a[1]).slice(0, 5);

  const links: QuickLink[] = [
    { path: '/setup-registration/matters', label: rtl ? 'كل المعاملات' : 'All matters', hint: rtl ? 'بحث، تصفية، إنشاء' : 'Search, filter, create', icon: <WorkspacesIcon fontSize="small" /> },
    { path: '/setup-registration/kanban/all', label: rtl ? 'لوحة كانبان' : 'Kanban board', hint: rtl ? 'سحب وإفلات لتقدّم المرحلة' : 'Drag-drop phase advance', icon: <KanbanIcon fontSize="small" /> },
    { path: '/setup-registration/grid', label: rtl ? 'شبكة الإعداد' : 'Setup grid', hint: rtl ? '٩٤×٣٥ شبكة قابلية التطبيق' : '94×35 applicability grid', icon: <GridIcon fontSize="small" /> },
    { path: '/setup-registration/dashboard', label: rtl ? 'لوحة الإعداد' : 'Setup dashboard', hint: rtl ? 'CRUD وعرض إداري' : 'Admin CRUD view', icon: <WorkspacesIcon fontSize="small" /> },
  ];

  return (
    <>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2 }}>
        <KpiTile accent={accent} label={rtl ? 'معاملات مفتوحة' : 'In-flight matters'} value={setup.isLoading ? '—' : setupList.length} loading={setup.isLoading} />
        <KpiTile accent={accent} label={rtl ? 'أنواع المعاملات' : 'Transaction types'} value="18" hint={rtl ? 'بحسب BRD §8.1.2' : 'per BRD §8.1.2'} />
        <KpiTile accent={accent} label={rtl ? 'خطوات ما بعد التأسيس' : 'Post-inc steps'} value="35" hint={rtl ? 'في الكتالوج المُبَذَّر' : 'seed catalogue'} />
        <KpiTile
          accent={accent}
          label={rtl ? 'تجاوز SLA' : 'SLA breaches'}
          value={sla.isLoading ? '—' : slaList.length}
          hint={rtl ? 'تنبيه فقط' : 'alerts only'}
          loading={sla.isLoading}
          intent={slaList.length > 0 ? 'warn' : 'default'}
        />
      </Box>

      <Typography variant="overline" sx={{ color: '#4A5568', letterSpacing: '0.06em', display: 'block', mb: 0.75 }}>
        {rtl ? 'إجراءات سريعة' : 'Quick actions'}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.25, mb: 2 }}>
        {links.map((l) => (
          <QuickTile key={l.path} link={l} accent={accent} onClick={onNav} count={l.path === '/setup-registration/matters' ? setupList.length : undefined} />
        ))}
      </Box>

      <Box sx={{ bgcolor: '#FFFFFF', border: CARD_BORDER, p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#0E1116' }}>
            {rtl ? 'توزيع المراحل النشطة' : 'Active phase distribution'}
          </Typography>
          <Typography variant="caption" sx={{ color: '#4A5568' }}>
            {rtl ? `${setupList.length} معاملة` : `${setupList.length} matters`}
          </Typography>
        </Stack>
        {setup.isLoading ? (
          <Skeleton variant="rectangular" height={80} />
        ) : topPhases.length === 0 ? (
          <Typography variant="body2" sx={{ color: '#506070' }}>
            {rtl ? 'لا توجد معاملات مفتوحة الآن' : 'No matters in flight right now'}
          </Typography>
        ) : (
          <Stack spacing={0.75}>
            {topPhases.map(([phase, n]) => {
              const pct = setupList.length ? (n / setupList.length) * 100 : 0;
              return (
                <Box key={phase}>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
                    <Typography variant="caption" sx={{ color: '#0E1116', fontFamily: 'monospace', fontSize: '0.72rem' }}>{phase}</Typography>
                    <Typography variant="caption" sx={{ color: '#4A5568' }}>{n}</Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={pct}
                    sx={{ height: 6, borderRadius: 0, bgcolor: '#EEF2F6', '& .MuiLinearProgress-bar': { bgcolor: accent } }}
                  />
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>
    </>
  );
};

// --- Retainer --------------------------------------------------------------

const RetainerBody: React.FC<{ accent: string; onNav: (p: string) => void; rtl: boolean }> = ({ accent, onNav, rtl }) => {
  const burn = usePortfolioBurnDown();
  const renewals = useUpcomingRenewals(30);
  const burnList = safeArray(burn.data);
  const renewalsList = safeArray(renewals.data);

  const totalHours = burnList.reduce((s, r) => s + (r.contract_hours || 0), 0);
  const usedHours = burnList.reduce((s, r) => s + (r.hours_used || 0), 0);
  const avgPct = burnList.length ? burnList.reduce((s, r) => s + r.pct_consumed, 0) / burnList.length : 0;
  const overage = burnList.filter((r) => r.pct_consumed >= 100).length;

  const links: QuickLink[] = [
    { path: '/retainer/list', label: rtl ? 'العقود' : 'Contracts', hint: rtl ? 'كل العقود النشطة' : 'All active contracts', icon: <WorkspacesIcon fontSize="small" /> },
    { path: '/retainer/timesheet', label: rtl ? 'الجدول الأسبوعي' : 'Timesheet', hint: rtl ? 'تسجيل الساعات' : 'Log billable hours', icon: <ScheduleIcon fontSize="small" /> },
    { path: '/retainer/renewals', label: rtl ? 'التجديدات' : 'Renewals', hint: rtl ? '٣٠ / ٦٠ / ٩٠ يوم' : '30 / 60 / 90 day window', icon: <LoopIcon fontSize="small" /> },
    { path: '/retainer/overages', label: rtl ? 'سجل التجاوز' : 'Overages', hint: rtl ? 'قرار شريك خلال ٣٠ يوم' : 'Partner decision within 30d', icon: <WarningIcon fontSize="small" /> },
  ];

  const sortedBurn = [...burnList].sort((a, b) => b.pct_consumed - a.pct_consumed).slice(0, 5);

  return (
    <>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2 }}>
        <KpiTile accent={accent} label={rtl ? 'العقود النشطة' : 'Active contracts'} value={burn.isLoading ? '—' : burnList.length} loading={burn.isLoading} />
        <KpiTile
          accent={accent}
          label={rtl ? 'متوسط الاستهلاك' : 'Avg consumption'}
          value={burn.isLoading ? '—' : `${avgPct.toFixed(0)}%`}
          hint={burn.isLoading ? '' : `${usedHours.toFixed(0)}/${totalHours.toFixed(0)} h`}
          loading={burn.isLoading}
          intent={avgPct >= 90 ? 'danger' : avgPct >= 75 ? 'warn' : 'default'}
        />
        <KpiTile
          accent={accent}
          label={rtl ? 'تجاوز > ١٠٠٪' : 'Over 100%'}
          value={burn.isLoading ? '—' : overage}
          hint={rtl ? 'يحتاج قرار الشريك' : 'needs partner decision'}
          loading={burn.isLoading}
          intent={overage > 0 ? 'danger' : 'default'}
        />
        <KpiTile
          accent={accent}
          label={rtl ? 'تجديدات خلال ٣٠ يوم' : 'Renewals (30d)'}
          value={renewals.isLoading ? '—' : renewalsList.length}
          hint={rtl ? 'تذكيرات للعميل' : 'client reminders'}
          loading={renewals.isLoading}
        />
      </Box>

      <Typography variant="overline" sx={{ color: '#4A5568', letterSpacing: '0.06em', display: 'block', mb: 0.75 }}>
        {rtl ? 'إجراءات سريعة' : 'Quick actions'}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.25, mb: 2 }}>
        {links.map((l) => (
          <QuickTile
            key={l.path}
            link={l}
            accent={accent}
            onClick={onNav}
            count={l.path === '/retainer/renewals' ? renewalsList.length : l.path === '/retainer/overages' ? overage : undefined}
          />
        ))}
      </Box>

      <Box sx={{ bgcolor: '#FFFFFF', border: CARD_BORDER, p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#0E1116' }}>
            {rtl ? 'أعلى استهلاك للساعات' : 'Top consumption'}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: accent, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            onClick={() => onNav('/retainer/list')}
          >
            {rtl ? 'كل العقود ←' : 'All contracts →'}
          </Typography>
        </Stack>
        {burn.isLoading ? (
          <Skeleton variant="rectangular" height={120} />
        ) : sortedBurn.length === 0 ? (
          <Typography variant="body2" sx={{ color: '#506070' }}>
            {rtl ? 'لا توجد عقود نشطة' : 'No active contracts'}
          </Typography>
        ) : (
          <Stack spacing={0.75}>
            {sortedBurn.map((r) => {
              const color = r.threshold === 'darkred' ? '#991B1B' : r.threshold === 'red' ? '#B45309' : r.threshold === 'amber' ? '#92400E' : accent;
              return (
                <Box key={r.id}>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
                    <Typography variant="caption" sx={{ color: '#0E1116' }} noWrap>{r.client_name}</Typography>
                    <Typography variant="caption" sx={{ color, fontWeight: 600 }}>{r.pct_consumed.toFixed(0)}%</Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(r.pct_consumed, 100)}
                    sx={{ height: 5, borderRadius: 0, bgcolor: '#EEF2F6', '& .MuiLinearProgress-bar': { bgcolor: color } }}
                  />
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>
    </>
  );
};

// --- Delegation ------------------------------------------------------------

const DelegationBody: React.FC<{ accent: string; onNav: (p: string) => void; rtl: boolean }> = ({ accent, onNav, rtl }) => {
  const links: QuickLink[] = [
    { path: '/delegation/set-deputy', label: rtl ? 'تعيين النائب' : 'Set my deputy', hint: rtl ? 'الطبقة الأولى — مفعّل عند الغياب' : 'Tier 1 — kicks in on OOO', icon: <PersonIcon fontSize="small" /> },
    { path: '/delegation/authorise-standing', label: rtl ? 'تفويض دائم' : 'Authorise standing', hint: rtl ? 'الطبقة الثانية — اشتراك صريح' : 'Tier 2 — explicit opt-in', icon: <GavelIcon fontSize="small" /> },
    { path: '/delegation/settings', label: rtl ? 'الإعدادات' : 'Settings', hint: rtl ? 'سياسات وحدود' : 'Policies + thresholds', icon: <WorkspacesIcon fontSize="small" /> },
  ];

  return (
    <>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 1.5, mb: 2 }}>
        <KpiTile
          accent={accent}
          label={rtl ? 'الطبقة الأولى' : 'Tier 1 deputies'}
          value="2"
          hint={rtl ? 'أبهيلاش / بطرس' : 'Abhilash / Boutros'}
        />
        <KpiTile
          accent={accent}
          label={rtl ? 'الطبقة الثانية (دائم)' : 'Tier 2 standing'}
          value="2"
          hint={rtl ? 'باميلا / إيليس' : 'Pamela / Elyse'}
        />
        <KpiTile
          accent={accent}
          label={rtl ? 'حد التصعيد' : 'Hard escalation'}
          value="﷼ 25,000"
          hint={rtl ? 'يذهب مباشرة للشريك المُدير' : 'goes straight to managing partner'}
          intent="warn"
        />
      </Box>

      <Typography variant="overline" sx={{ color: '#4A5568', letterSpacing: '0.06em', display: 'block', mb: 0.75 }}>
        {rtl ? 'إجراءات' : 'Actions'}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.25, mb: 2 }}>
        {links.map((l) => <QuickTile key={l.path} link={l} accent={accent} onClick={onNav} />)}
      </Box>

      <Box sx={{ bgcolor: SUBTLE_BG, border: CARD_BORDER, p: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#0E1116', mb: 0.75 }}>
          {rtl ? 'كيف يعمل التفويض' : 'How delegation works'}
        </Typography>
        <Stack spacing={0.75}>
          <Typography variant="body2" sx={{ color: '#4A5568', fontSize: '0.82rem' }}>
            <strong>{rtl ? 'الطبقة الأولى:' : 'Tier 1:'}</strong>{' '}
            {rtl
              ? 'أي طلب يطلب موافقتك يتم توجيهه تلقائياً إلى نائبك المُعيَّن عند تفعيل وضع الغياب.'
              : 'Any request awaiting your approval auto-routes to your nominated deputy when you activate OOO mode.'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#4A5568', fontSize: '0.82rem' }}>
            <strong>{rtl ? 'الطبقة الثانية:' : 'Tier 2:'}</strong>{' '}
            {rtl
              ? 'تفويض دائم لشخص محدد لاتخاذ قرارات نيابة عنك ضمن نطاق وفترة محددتين — يجب الموافقة عليه صراحةً.'
              : 'A standing authorisation for a specific person to act on your behalf within a defined scope + time window — requires explicit opt-in.'}
          </Typography>
          <Divider sx={{ my: 0.5 }} />
          <Typography variant="caption" sx={{ color: '#506070', fontSize: '0.74rem' }}>
            {rtl
              ? 'الموافقات بقيمة تتجاوز ٢٥٬٠٠٠ ﷼ تتجاوز سلسلة التفويض دائماً وتذهب إلى الشريك المُدير حصرياً (FR-079).'
              : 'Approvals at or above SR 25,000 always bypass the deputy chain and route to the managing partner only (FR-079).'}
          </Typography>
        </Stack>
      </Box>
    </>
  );
};

// --- Documents -------------------------------------------------------------

const DocumentsBody: React.FC<{ accent: string; onNav: (p: string) => void; rtl: boolean }> = ({ accent, onNav, rtl }) => {
  const docs = useDocuments();
  const expiring = useMyDocsExpiring();
  const docsList: Array<{ id: string; title: string; document_type: string | null; source_type: string | null; expiry_date: string | null; updated_at: string }> =
    Array.isArray(docs.data) ? docs.data : [];
  const expiringList = safeArray(expiring.data);

  const fromEmail = docsList.filter((d) => d.source_type === 'email' || d.source_type === 'email_attachment').length;
  const expiring30 = expiringList.filter((d) => d.days_until_expiry <= 30).length;

  const byType = docsList.reduce<Record<string, number>>((acc, d) => {
    const k = (d.document_type || 'untyped').toLowerCase();
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});
  const topTypes = Object.entries(byType).sort((a, b) => b[1] - a[1]).slice(0, 5);

  const recent = [...docsList]
    .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
    .slice(0, 5);

  const links: QuickLink[] = [
    { path: '/documents/browse', label: rtl ? 'كل المستندات' : 'All documents', hint: rtl ? 'بحث، فلترة، عرض' : 'Browse, search, filter', icon: <FolderIcon fontSize="small" /> },
    { path: '/documents/browse?expiring=60', label: rtl ? 'تنتهي قريباً' : 'Expiring soon', hint: rtl ? 'خلال ٦٠ يوماً' : 'Within 60 days', icon: <EventBusyIcon fontSize="small" /> },
    { path: '/documents/browse?source=email', label: rtl ? 'مرفقات البريد' : 'Email attachments', hint: rtl ? 'مُستخرجة تلقائياً' : 'Auto-ingested from triage', icon: <AttachIcon fontSize="small" /> },
    { path: '/admin/document-templates', label: rtl ? 'قوالب المستندات' : 'Document templates', hint: rtl ? 'POA / AOA / حجز اسم' : 'POA / AOA / Name reservation', icon: <ArticleIcon fontSize="small" /> },
  ];

  return (
    <>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2 }}>
        <KpiTile
          accent={accent}
          label={rtl ? 'إجمالي المستندات' : 'Total documents'}
          value={docs.isLoading ? '—' : docsList.length}
          hint={rtl ? 'في المكتبة' : 'in library'}
          loading={docs.isLoading}
        />
        <KpiTile
          accent={accent}
          label={rtl ? 'تنتهي خلال ٦٠ يوماً' : 'Expiring (60d)'}
          value={expiring.isLoading ? '—' : expiringList.length}
          hint={rtl ? `${expiring30} خلال ٣٠ يوماً` : `${expiring30} within 30d`}
          loading={expiring.isLoading}
          intent={expiring30 > 0 ? 'warn' : 'default'}
        />
        <KpiTile
          accent={accent}
          label={rtl ? 'مرفقات بريد' : 'Email-sourced'}
          value={docs.isLoading ? '—' : fromEmail}
          hint={rtl ? 'استخراج تلقائي' : 'auto-ingested'}
          loading={docs.isLoading}
        />
        <KpiTile
          accent={accent}
          label={rtl ? 'أنواع المستندات' : 'Document types'}
          value={docs.isLoading ? '—' : Object.keys(byType).length}
          hint={rtl ? 'إقامة، CR، POA…' : 'iqama, CR, POA…'}
          loading={docs.isLoading}
        />
      </Box>

      <Typography variant="overline" sx={{ color: '#4A5568', letterSpacing: '0.06em', display: 'block', mb: 0.75 }}>
        {rtl ? 'إجراءات سريعة' : 'Quick actions'}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.25, mb: 2 }}>
        {links.map((l) => (
          <QuickTile
            key={l.path}
            link={l}
            accent={accent}
            onClick={onNav}
            count={
              l.path === '/documents/browse' ? docsList.length :
              l.path === '/documents/browse?expiring=60' ? expiringList.length :
              l.path === '/documents/browse?source=email' ? fromEmail :
              undefined
            }
          />
        ))}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
        {/* Recent uploads */}
        <Box sx={{ bgcolor: '#FFFFFF', border: CARD_BORDER, p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#0E1116' }}>
              {rtl ? 'آخر التحديثات' : 'Recent activity'}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: accent, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
              onClick={() => onNav('/documents/browse')}
            >
              {rtl ? 'كل المستندات ←' : 'All documents →'}
            </Typography>
          </Stack>
          {docs.isLoading ? (
            <Skeleton variant="rectangular" height={120} />
          ) : recent.length === 0 ? (
            <Typography variant="body2" sx={{ color: '#506070' }}>
              {rtl ? 'لا توجد مستندات بعد' : 'No documents yet'}
            </Typography>
          ) : (
            <Stack divider={<Divider />}>
              {recent.map((d) => (
                <Stack
                  key={d.id}
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ py: 0.5, cursor: 'pointer', '&:hover': { bgcolor: '#FAFAF7' } }}
                  onClick={() => onNav(`/documents/${d.id}`)}
                >
                  <Typography variant="body2" sx={{ flex: 1, color: '#0E1116', fontSize: '0.82rem' }} noWrap>
                    {d.title || '—'}
                  </Typography>
                  {d.document_type ? (
                    <Chip
                      size="small"
                      label={d.document_type}
                      sx={{ height: 18, fontSize: '0.66rem', bgcolor: `${accent}14`, color: accent, fontFamily: 'monospace' }}
                    />
                  ) : null}
                </Stack>
              ))}
            </Stack>
          )}
        </Box>

        {/* Expiring next */}
        <Box sx={{ bgcolor: '#FFFFFF', border: CARD_BORDER, p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#0E1116' }}>
              {rtl ? 'الأقرب لانتهاء الصلاحية' : 'Next to expire'}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: accent, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
              onClick={() => onNav('/documents/browse?expiring=60')}
            >
              {rtl ? 'كلها ←' : 'All →'}
            </Typography>
          </Stack>
          {expiring.isLoading ? (
            <Skeleton variant="rectangular" height={120} />
          ) : expiringList.length === 0 ? (
            <Typography variant="body2" sx={{ color: '#506070' }}>
              {rtl ? 'لا توجد مستندات قريبة الانتهاء' : 'Nothing expiring in the next 60 days'}
            </Typography>
          ) : (
            <Stack divider={<Divider />}>
              {expiringList.slice(0, 5).map((d) => {
                const urgent = d.days_until_expiry <= 14;
                const warn = d.days_until_expiry <= 30;
                const color = urgent ? '#991B1B' : warn ? '#92400E' : '#4A5568';
                return (
                  <Stack
                    key={d.id}
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ py: 0.5, cursor: 'pointer', '&:hover': { bgcolor: '#FAFAF7' } }}
                    onClick={() => onNav(`/documents/${d.id}`)}
                  >
                    <Typography variant="body2" sx={{ flex: 1, color: '#0E1116', fontSize: '0.82rem' }} noWrap>
                      {d.title}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#506070', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                      {d.expiry_date}
                    </Typography>
                    <Typography variant="caption" sx={{ color, fontWeight: 600, fontSize: '0.72rem', minWidth: 36, textAlign: 'right' }}>
                      {d.days_until_expiry}d
                    </Typography>
                  </Stack>
                );
              })}
            </Stack>
          )}
        </Box>
      </Box>
    </>
  );
};

// --- Recent emails (jylaw) -------------------------------------------------

interface EmailRow { id: string; subject: string; from_address: string; received_at: string; status: string; }

const RecentEmailsPanel: React.FC<{
  rtl: boolean;
  accent: string;
  emails: EmailRow[];
  loading: boolean;
  onNav: (p: string) => void;
}> = ({ rtl, accent, emails, loading, onNav }) => (
  <Box sx={{ bgcolor: '#FFFFFF', border: CARD_BORDER, p: 2 }}>
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
      <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#0E1116' }}>
        {rtl ? 'آخر رسائل البريد' : 'Recent emails'}
      </Typography>
      <Typography
        variant="caption"
        sx={{ color: accent, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
        onClick={() => onNav('/email-inbox')}
      >
        {rtl ? 'صندوق البريد ←' : 'Open inbox →'}
      </Typography>
    </Stack>
    {loading ? (
      <Skeleton variant="rectangular" height={100} />
    ) : emails.length === 0 ? (
      <Typography variant="body2" sx={{ color: '#506070' }}>
        {rtl ? 'لا توجد رسائل حديثة' : 'No recent emails'}
      </Typography>
    ) : (
      <Stack divider={<Divider />}>
        {emails.map((e) => (
          <Stack key={e.id} direction="row" alignItems="center" spacing={1} sx={{ py: 0.5 }}>
            <Typography variant="body2" sx={{ color: '#0E1116', flex: 1, fontSize: '0.82rem' }} noWrap>
              {e.subject || '—'}
            </Typography>
            <Typography variant="caption" sx={{ color: '#506070', fontFamily: 'monospace', fontSize: '0.7rem' }} noWrap>
              {e.from_address}
            </Typography>
            <Chip
              size="small"
              label={e.status}
              sx={{ height: 18, fontSize: '0.66rem', bgcolor: e.status === 'classified' ? '#FEF3C7' : '#E0F2FE', color: '#0E1116' }}
            />
          </Stack>
        ))}
      </Stack>
    )}
  </Box>
);

// --- Legal Services --------------------------------------------------------

const LEGAL_SERVICES_CODE = 'legal_services';

const LegalServicesBody: React.FC<{ accent: string; onNav: (p: string) => void; rtl: boolean }> = ({ accent, onNav, rtl }) => {
  const { data: allMatters, isLoading: mattersLoading } = useSetupMatters({ activeOnly: false });
  const { data: txTypes, isLoading: txLoading } = useTransactionTypes({ activeOnly: true });

  const isLoading = mattersLoading || txLoading;

  // Filter tx types to legal_services service line
  const legalTxIds = React.useMemo(() => {
    if (!txTypes) return new Set<string>();
    return new Set(
      (txTypes as Array<{ id: string; service_line_code?: string }>)
        .filter((tt) => tt.service_line_code === LEGAL_SERVICES_CODE)
        .map((tt) => tt.id),
    );
  }, [txTypes]);

  const legalTxTypes = React.useMemo(() => {
    if (!txTypes) return [];
    return (txTypes as Array<{ id: string; code: string; name_en: string; name_ar: string; service_line_code?: string }>)
      .filter((tt) => tt.service_line_code === LEGAL_SERVICES_CODE);
  }, [txTypes]);

  // Legal matters (filtered)
  const legalMatters = React.useMemo(() => {
    if (!allMatters) return [];
    return allMatters.filter((m) => legalTxIds.has(m.transaction_type_id));
  }, [allMatters, legalTxIds]);

  // KPIs
  const openMatters = legalMatters.filter((m) => m.status !== 'done' && m.status !== 'cancelled').length;

  const now = new Date();
  const quarterStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
  const completedThisQuarter = legalMatters.filter((m) => {
    if (m.status !== 'done') return false;
    // updated_at or started_at as proxy — use whatever field exists
    const ts = (m as unknown as { updated_at?: string }).updated_at;
    return ts ? new Date(ts) >= quarterStart : false;
  }).length;

  const recentMatters = React.useMemo(
    () =>
      [...legalMatters]
        .sort((a, b) => {
          const ta = (a as unknown as { updated_at?: string }).updated_at ?? '';
          const tb = (b as unknown as { updated_at?: string }).updated_at ?? '';
          return tb.localeCompare(ta);
        })
        .slice(0, 10),
    [legalMatters],
  );

  const links: QuickLink[] = [
    { path: '/legal-services/matters', label: rtl ? 'كل القضايا' : 'All Matters', hint: rtl ? 'بحث، تصفية، إنشاء' : 'Search, filter, create', icon: <WorkspacesIcon fontSize="small" /> },
    { path: '/legal-services/log-hours', label: rtl ? 'تسجيل الساعات' : 'Log Hours', hint: rtl ? 'ساعات بموضوع قضية' : 'Matter-based hour logging', icon: <AccessTimeIcon fontSize="small" /> },
    { path: '/legal-services/kanban/all', label: rtl ? 'لوحة كانبان' : 'Kanban Board', hint: rtl ? 'سحب وإفلات تقدم المراحل' : 'Drag-drop phase advance', icon: <KanbanIcon fontSize="small" /> },
    { path: '/billing/invoices', label: rtl ? 'الفواتير' : 'Invoices', hint: rtl ? 'فواتير الخدمات القانونية' : 'Legal services billing', icon: <BillingIcon fontSize="small" /> },
  ];

  return (
    <>
      {/* KPI row */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2 }}>
        <KpiTile
          accent={accent}
          label={rtl ? 'القضايا المفتوحة' : 'Open Matters'}
          value={isLoading ? '—' : openMatters}
          hint={rtl ? 'قيد التنفيذ الآن' : 'currently in flight'}
          loading={isLoading}
        />
        <KpiTile
          accent={accent}
          label={rtl ? 'ساعات هذا الشهر' : 'Hours This Month'}
          value="—"
          hint={rtl ? 'يحتاج API W2.5' : 'hours-summary endpoint W2.5'}
        />
        <KpiTile
          accent={accent}
          label={rtl ? 'مكتملة هذا الربع' : 'Completed This Quarter'}
          value={isLoading ? '—' : completedThisQuarter}
          loading={isLoading}
        />
        <KpiTile
          accent={accent}
          label={rtl ? 'أنواع المعاملات' : 'Transaction Types'}
          value={isLoading ? '—' : legalTxTypes.length}
          hint={rtl ? 'الخدمات المتاحة' : 'available services'}
          loading={txLoading}
        />
      </Box>

      {/* Quick actions */}
      <Typography variant="overline" sx={{ color: '#4A5568', letterSpacing: '0.06em', display: 'block', mb: 0.75 }}>
        {rtl ? 'إجراءات سريعة' : 'Quick actions'}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.25, mb: 2 }}>
        {links.map((l) => (
          <QuickTile
            key={l.path}
            link={l}
            accent={accent}
            onClick={onNav}
            count={l.path === '/legal-services/matters' ? (isLoading ? undefined : openMatters) : undefined}
          />
        ))}
      </Box>

      {/* Quick-launch grid: one card per legal_services transaction type */}
      <Typography variant="overline" sx={{ color: '#4A5568', letterSpacing: '0.06em', display: 'block', mb: 0.75 }}>
        {rtl ? 'بدء قضية جديدة' : 'Start a New Matter'}
      </Typography>
      {txLoading ? (
        <Skeleton variant="rectangular" height={72} sx={{ mb: 2 }} />
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 1.25, mb: 2 }}>
          {legalTxTypes.map((tt) => (
            <QuickTile
              key={tt.id}
              link={{
                path: `/legal-services/kanban/${tt.code}`,
                label: rtl ? tt.name_ar : tt.name_en,
                icon: <GavelIcon fontSize="small" />,
              }}
              accent={accent}
              onClick={onNav}
            />
          ))}
          {legalTxTypes.length === 0 && (
            <Typography variant="body2" sx={{ color: '#506070' }}>
              {rtl ? 'لا توجد أنواع معاملات قانونية' : 'No legal services transaction types seeded yet'}
            </Typography>
          )}
        </Box>
      )}

      {/* Recent matters list */}
      <Box sx={{ bgcolor: '#FFFFFF', border: CARD_BORDER, p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#0E1116' }}>
            {rtl ? 'القضايا الأخيرة' : 'Recent Matters'}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: accent, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            onClick={() => onNav('/legal-services/matters')}
          >
            {rtl ? 'كل القضايا ←' : 'All matters →'}
          </Typography>
        </Stack>
        {isLoading ? (
          <Skeleton variant="rectangular" height={120} />
        ) : recentMatters.length === 0 ? (
          <Typography variant="body2" sx={{ color: '#506070' }}>
            {rtl ? 'لا توجد قضايا قانونية بعد' : 'No legal matters yet'}
          </Typography>
        ) : (
          <Stack divider={<Divider />}>
            {recentMatters.map((m) => {
              const txType = legalTxTypes.find((tt) => tt.id === m.transaction_type_id);
              return (
                <Stack
                  key={m.id}
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ py: 0.5, cursor: 'pointer', '&:hover': { bgcolor: '#FAFAF7' } }}
                  onClick={() => onNav(`/legal-services/matters/${m.id}`)}
                >
                  <Typography variant="body2" sx={{ flex: 1, color: '#0E1116', fontSize: '0.82rem' }} noWrap>
                    {(m as unknown as { client_company_id: string }).client_company_id.slice(0, 8)}…
                  </Typography>
                  {txType ? (
                    <Chip
                      size="small"
                      label={rtl ? txType.name_ar : txType.name_en}
                      sx={{ height: 18, fontSize: '0.66rem', bgcolor: `${accent}14`, color: accent }}
                    />
                  ) : null}
                  <Chip
                    size="small"
                    label={m.status}
                    sx={{ height: 18, fontSize: '0.66rem' }}
                  />
                </Stack>
              );
            })}
          </Stack>
        )}
      </Box>
    </>
  );
};

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------

const ModuleHome: React.FC<ModuleHomeProps> = ({ moduleId }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const { direction } = useUiLanguageStore();
  const rtl = direction === 'rtl';
  const config = MODULE_CONFIGS[moduleId as ModuleId];

  if (!config) {
    return <Typography>{t('common.error', { defaultValue: 'Module not found' })}</Typography>;
  }

  const moduleLabel = t(`modules.${moduleId}.label`, { defaultValue: config.label });
  const moduleDescription = t(`modules.${moduleId}.description`, { defaultValue: config.description });
  const onNav = (path: string) => navigate(path);

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, md: 3 }, py: 3 }} dir={direction}>
      <Box sx={{ mb: 2.5 }}>
        <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ flexWrap: 'wrap' }}>
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{
              color: config.accent,
              fontFamily: '"Source Serif 4", "Source Serif Pro", "Noto Naskh Arabic", Georgia, serif',
              letterSpacing: '-0.01em',
            }}
          >
            {moduleLabel}
          </Typography>
          <Box sx={{ height: 16, width: 1, bgcolor: '#D1D5DB' }} />
          <Typography variant="body2" sx={{ color: '#4A5568' }}>
            {moduleDescription}
          </Typography>
        </Stack>
      </Box>

      {moduleId === 'jylaw' && <JylawBody accent={config.accent} onNav={onNav} rtl={rtl} />}
      {moduleId === 'setup-registration' && <SetupBody accent={config.accent} onNav={onNav} rtl={rtl} />}
      {moduleId === 'retainer' && <RetainerBody accent={config.accent} onNav={onNav} rtl={rtl} />}
      {moduleId === 'delegation' && <DelegationBody accent={config.accent} onNav={onNav} rtl={rtl} />}
      {moduleId === 'documents' && <DocumentsBody accent={config.accent} onNav={onNav} rtl={rtl} />}

      {moduleId === 'legal-services' && <LegalServicesBody accent={config.accent} onNav={onNav} rtl={rtl} />}

  {!['jylaw', 'setup-registration', 'retainer', 'delegation', 'documents', 'legal-services'].includes(moduleId) && (
        <Typography variant="body2" sx={{ color: '#4A5568' }}>
          {t('sidebar.useToNavigate', { defaultValue: 'Use the sidebar to navigate entities.' })}
        </Typography>
      )}
    </Box>
  );
};

export default ModuleHome;
